import { useEffect } from 'react';

import { batch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PUBLIC_PAGES } from 'general/constants';
import { cloneDeep } from 'lodash';
import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { setExchangeSettings, setPaymentSettings } from 'modules/eecs-go';
import { setGrexAccounts } from 'modules/grex';
import { setHknrAccount } from 'modules/hknr';
import { setNecsAccount } from 'modules/necs';

import {
  useChangePasswordMutation,
  useChangePasswordRequestMutation,
  useChangePasswordVerifyOtpMutation,
  useConfirmEnableTwoFaAppMutation,
  useConfirmEnableTwoFaEmailMutation,
  useDisableTwoFaAppMutation,
  useLazyCheckSessionQuery,
  useLazyEnableTwoFaAppQuery,
  useLazyGetCSRFCookieQuery,
  useLazyLogoutQuery,
  useLazyResetTwoFaAppQuery,
  useLoginMutation,
  useResetPasswordMutation,
  useSetNewPasswordMutation,
  useSignupMutation,
  useVerifyAppOtpMutation,
  useVerifyOtpMutation,
} from '../api/auth-session.api';
import { setCurrentUser, setIsLoading, setUserIdDocumentModalOpen } from '../store/auth-session.slice';
import {
  ChangePasswordPayload,
  EUserRoles,
  EnableTwoFaAppResponse,
  IUser,
  LoginPayload,
  LoginResponse,
  ResetPasswordPayload,
  ResetTwoFaPayload,
  SetNewPasswordPayload,
  SignupPayload,
  VerifyOtpPayload,
} from '../types/auth-session.types';

interface UseAuthSession {
  loginHandler: (payload: LoginPayload) => Promise<LoginResponse | null>;
  signupHandler: (payload: SignupPayload) => Promise<IUser | null>;
  logoutHandler: () => Promise<void>;
  getCSRFHandler: () => Promise<void>;
  checkSessionHandler: () => Promise<boolean | null>;
  checkAuth: () => Promise<void>;
  verifyOtpHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
  enableTwoFaAppHandler: () => Promise<EnableTwoFaAppResponse | null>;
  confirmEnableTwoFaAppHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
  confirmEnableTwoFaEmailHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
  disableTwoFaAppHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
  resetTwoFaAppHandler: (payload: ResetTwoFaPayload) => Promise<boolean>;
  verifyAppOtpHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
  resetPasswordHandler: (payload: ResetPasswordPayload) => Promise<boolean>;
  setNewPasswordHandler: (payload: SetNewPasswordPayload) => Promise<boolean>;
  changePasswordHandler: (payload: ChangePasswordPayload) => Promise<boolean>;
  changePasswordRequestHandler: () => Promise<boolean>;
  changePasswordVerifyOtpHandler: (payload: VerifyOtpPayload) => Promise<boolean>;
}

export const useAuthSession = (): UseAuthSession => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { featureSettings } = useAppSelector((store) => store.platform);

  const [signupQuery] = useSignupMutation();
  const [loginQuery] = useLoginMutation();
  const [logoutQuery] = useLazyLogoutQuery();
  const [getCSRFQuery] = useLazyGetCSRFCookieQuery();
  const [checkSessionQuery] = useLazyCheckSessionQuery();
  const [verifyOtpQuery] = useVerifyOtpMutation();
  const [enableTwoFaAppQuery] = useLazyEnableTwoFaAppQuery();
  const [confirmEnableTwoFaAppQuery] = useConfirmEnableTwoFaAppMutation();
  const [confirmEnableTwoFaEmailQuery] = useConfirmEnableTwoFaEmailMutation();
  const [disableTwoFaAppQuery] = useDisableTwoFaAppMutation();
  const [resetTwoFaAppQuery] = useLazyResetTwoFaAppQuery();
  const [verifyAppOtpQuery] = useVerifyAppOtpMutation();
  const [resetPasswordQuery] = useResetPasswordMutation();
  const [setNewPasswordQuery] = useSetNewPasswordMutation();

  const [changePasswordQuery] = useChangePasswordMutation();
  const [changePasswordRequestQuery] = useChangePasswordRequestMutation();
  const [changePasswordVerifyOtpQuery] = useChangePasswordVerifyOtpMutation();

  const changePasswordHandler = async (payload: ChangePasswordPayload) => {
    const { error } = await changePasswordQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to change password');
      return false;
    }
    return true;
  };

  const changePasswordRequestHandler = async () => {
    const { error } = await changePasswordRequestQuery();
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to change password');
      return false;
    }
    return true;
  };

  const changePasswordVerifyOtpHandler = async (payload: VerifyOtpPayload) => {
    const { error } = await changePasswordVerifyOtpQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to verify otp');
      return false;
    }
    return true;
  };

  const resetPasswordHandler = async (payload: ResetPasswordPayload) => {
    const { error } = await resetPasswordQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to reset password');
      return false;
    }
    toast.success('Email for password reset sent successfully');
    return true;
  };

  const setNewPasswordHandler = async (payload: SetNewPasswordPayload) => {
    const { error } = await setNewPasswordQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to set new password');
      return false;
    }
    toast.success('New password set successfully');
    return true;
  };

  const verifyAppOtpHandler = async (payload: VerifyOtpPayload) => {
    const { error } = await verifyAppOtpQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to verify otp');
      return false;
    }
    return true;
  };

  const resetTwoFaAppHandler = async (payload: ResetTwoFaPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await resetTwoFaAppQuery(payload);
    dispatch(setIsLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to reset 2FA');
      return false;
    }
    toast.success('2FA successfully resets');
    return true;
  };

  const confirmEnableTwoFaEmailHandler = async (payload: VerifyOtpPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await confirmEnableTwoFaEmailQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to confirm 2fa email');
      dispatch(setIsLoading(false));
      return false;
    }
    await checkSessionHandler();
    dispatch(setIsLoading(false));
    toast.success('2FA successfully switched to email');
    return true;
  };

  const disableTwoFaAppHandler = async (payload: VerifyOtpPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await disableTwoFaAppQuery(payload);
    dispatch(setIsLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to disable 2fa app');
      return false;
    }
    toast.success('2FA APP verified. Check your email for the verification code.');
    return true;
  };

  const confirmEnableTwoFaAppHandler = async (payload: VerifyOtpPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await confirmEnableTwoFaAppQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to confirm 2fa app');
      dispatch(setIsLoading(false));
      return false;
    }
    await checkSessionHandler();
    dispatch(setIsLoading(false));
    toast.success('2FA APP verified');
    return true;
  };

  const enableTwoFaAppHandler = async () => {
    dispatch(setIsLoading(true));
    const { error, data: response } = await enableTwoFaAppQuery();
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to enable 2fa app');
      return null;
    }
    return response.data;
  };

  const verifyOtpHandler = async (payload: VerifyOtpPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await verifyOtpQuery(payload);
    dispatch(setIsLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to verify otp');
      return false;
    }
    return true;
  };

  const getCSRFHandler = async () => {
    const { error } = await getCSRFQuery();
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to get csrf');
    }
  };

  const checkSessionHandler = async () => {
    const { data: response, error } = await checkSessionQuery();

    if (error && 'status' in error && error.status === 401) {
      return false;
    }
    if (error || !response?.data) {
      return null;
    }

    const currentUser = response.data;
    batch(() => {
      dispatch(setCurrentUser(currentUser));
      dispatch(setNecsAccount(currentUser?.company?.necs_account ? cloneDeep(currentUser.company.necs_account) : null));
      dispatch(setExchangeSettings(currentUser?.exchange_settings ? cloneDeep(currentUser.exchange_settings) : null));
      currentUser?.company?.grex_accounts && dispatch(setGrexAccounts(cloneDeep(currentUser.company.grex_accounts)));
      dispatch(setHknrAccount(currentUser?.company?.hknr_account ? cloneDeep(currentUser.company.hknr_account) : null));
      dispatch(
        setPaymentSettings(
          currentUser?.company?.payment_settings ? cloneDeep(currentUser.company.payment_settings) : null,
        ),
      );

      if (
        !currentUser.id_document &&
        !currentUser.roles.map((role) => role.name.toLowerCase()).includes(EUserRoles.ADMIN)
      ) {
        dispatch(setUserIdDocumentModalOpen(true));
      }
    });

    return true;
  };

  const checkAuth = async () => {
    const result = await checkSessionHandler();
    if (result === null) return;
    const { pathname } = window.location;
    if (result && PUBLIC_PAGES.includes(pathname)) {
      window.location.href = featureSettings.default_login_page || '/';
    }
    if (!result && PUBLIC_PAGES.includes(pathname)) {
      return;
    }
    if (!result && !PUBLIC_PAGES.includes(pathname)) {
      window.location.href = '/login';
    }
  };

  const loginHandler = async (payload: LoginPayload) => {
    dispatch(setIsLoading(true));
    await getCSRFHandler();
    const { error, data: response } = await loginQuery(payload);
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to login!');
      return null;
    }
    return response.data;
  };

  const signupHandler = async (payload: SignupPayload) => {
    dispatch(setIsLoading(true));
    const { error, data: response } = await signupQuery(payload);
    dispatch(setIsLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to signup!');
      return null;
    }
    return response.data;
  };

  const logoutHandler = async () => {
    const { error } = await logoutQuery();
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to logout!');
      return;
    }
    batch(() => {
      dispatch(setCurrentUser(null));
      dispatch(setNecsAccount(null));
      dispatch(setIsLoading(false));
      dispatch(setExchangeSettings(null));
      dispatch(setHknrAccount(null));
      dispatch(setPaymentSettings(null));
    });

    history.push('/login');
  };

  useEffect(() => {}, []);

  return {
    loginHandler,
    signupHandler,
    checkSessionHandler,
    logoutHandler,
    getCSRFHandler,
    checkAuth,
    verifyOtpHandler,
    enableTwoFaAppHandler,
    confirmEnableTwoFaAppHandler,
    confirmEnableTwoFaEmailHandler,
    disableTwoFaAppHandler,
    resetTwoFaAppHandler,
    verifyAppOtpHandler,
    resetPasswordHandler,
    setNewPasswordHandler,
    changePasswordHandler,
    changePasswordRequestHandler,
    changePasswordVerifyOtpHandler,
  };
};
